import {
  APIComponentType,
  APIModelAttributes,
  type APIReferenceType,
  ArdoqId,
  ArrowType,
  IMPLICIT_REFTYPE_ID,
  LineType,
  isArrowType,
} from '@ardoq/api-types';
import {
  ComponentTypesById,
  type ComponentTypeWithoutChildren,
  type DecomposedComponentTypes,
  DEFAULT_REFERENCE_TYPES,
  ModelTypes,
  ComponentTypes,
  ReferenceTypes,
  referenceTypeDefaultColor,
  referenceTypeDefaultLineType,
} from '@ardoq/data-model';

const flattenTypeTree = (
  typeList: APIComponentType[]
): [ArdoqId, APIComponentType][] => {
  return typeList.flatMap(type => [
    [type.id, type],
    ...flattenTypeTree(Object.values(type.children)),
  ]) as [ArdoqId, APIComponentType][];
};

const toComponentTypeDictionary = (
  root: Record<ArdoqId, APIComponentType>
): ComponentTypesById =>
  Object.fromEntries(flattenTypeTree(Object.values(root)));

export const getComponentTypes = (
  model: APIModelAttributes
): ComponentTypes => {
  const componentTypeDictionary = toComponentTypeDictionary(model.root);
  return {
    componentTypes: Object.keys(componentTypeDictionary),
    componentTypesById: componentTypeDictionary,
  };
};

const toComponentTypeWithoutChildren = ({
  children: _children,
  ...componentTypeWithoutChildren
}: APIComponentType): ComponentTypeWithoutChildren =>
  componentTypeWithoutChildren;

export const getDecomposedComponentTypes = (
  model: APIModelAttributes
): DecomposedComponentTypes => {
  const componentTypes: Record<string, ComponentTypeWithoutChildren> = {};
  const childrenIndex: Record<string, string[]> = {};

  const componentTypesById = getComponentTypes(model).componentTypesById;
  for (const componentType of Object.values(componentTypesById)) {
    const childrenIds = Object.keys(componentType.children);
    const existingChildren = childrenIndex[componentType.id] ?? [];
    childrenIndex[componentType.id] = Array.from(
      new Set([...existingChildren, ...childrenIds])
    );

    componentTypes[componentType.id] =
      toComponentTypeWithoutChildren(componentType);
  }

  return {
    componentTypes,
    childrenIndex: {
      ...childrenIndex,
      root: Object.keys(model.root),
    },
  };
};

export const getComponentTypesWithParents = (
  model: APIModelAttributes
): ComponentTypes => {
  const componentTypeDictionary = toComponentTypeDictionary(
    toTypesWithParents(model.root)
  );
  return {
    componentTypes: Object.keys(componentTypeDictionary),
    componentTypesById: componentTypeDictionary,
  };
};

const toTypesWithParents = (
  root: Record<ArdoqId, APIComponentType>
): Record<ArdoqId, APIComponentType> => traverse(root, null);

const traverse = (
  types: Record<ArdoqId, APIComponentType>,
  parent: ArdoqId | null,
  result: Record<ArdoqId, APIComponentType> = {}
) => {
  for (const typeId in types) {
    const type = types[typeId];
    result[typeId] = { ...type, parent };
    traverse(type.children, typeId, result);
  }
  return result;
};

export const getReferenceTypes = (
  model: APIModelAttributes
): ReferenceTypes => {
  const referenceTypeDictionary = model.referenceTypes;
  return {
    referenceTypes: Object.keys(referenceTypeDictionary),

    referenceTypesById: referenceTypeDictionary,
  };
};

export const getModelTypes = (model: APIModelAttributes): ModelTypes => ({
  ...getReferenceTypes(model),
  ...getComponentTypes(model),
});

export const getRefTypeById = (
  referenceTypes: APIReferenceType[],
  id: number
): APIReferenceType | null => referenceTypes.find(rt => rt.id === id) ?? null;

export const getRefTypeName = (
  referenceTypes: APIReferenceType[],
  typeId: number
): string | null => getRefTypeById(referenceTypes, typeId)?.name ?? null;

export const getRefType = (
  referenceTypes: APIReferenceType[],
  typeId: number
): APIReferenceType | null =>
  getRefTypeById(referenceTypes, typeId) ??
  getRefTypeById(referenceTypes, IMPLICIT_REFTYPE_ID);

export const getModelType = (
  referenceTypes: APIReferenceType[],
  typeId: number
): APIReferenceType =>
  getRefType(referenceTypes, typeId) ??
  DEFAULT_REFERENCE_TYPES[IMPLICIT_REFTYPE_ID];

export const getLineBeginning = (
  referenceType: APIReferenceType | null,
  sourceCardinality?: ArrowType | null,
  isAggregated?: boolean
): ArrowType => {
  if (referenceType?.hasCardinality) {
    return isAggregated
      ? ArrowType.NONE
      : (sourceCardinality ?? referenceType.lineBeginning ?? ArrowType.NONE);
  }
  return referenceType?.lineBeginning ?? ArrowType.NONE;
};

export const getLineEnding = (
  referenceType: APIReferenceType | null,
  targetCardinality?: ArrowType | null,
  isAggregated?: boolean
): ArrowType => {
  const lineEndingCandidate = referenceType?.lineEnding;
  const lineEnding = isArrowType(lineEndingCandidate)
    ? lineEndingCandidate
    : ArrowType.NONE;

  if (referenceType?.hasCardinality) {
    return isAggregated ? ArrowType.NONE : (targetCardinality ?? lineEnding);
  }
  return lineEnding;
};

export const lineTypeToStrokeDasharray = (
  line: LineType | undefined
): string | undefined => {
  if (line === LineType.DASHED) return '10,5';
  if (line === LineType.DOTTED) return '2';
  return undefined;
};

export const getReferenceTypeSvgStyle = (
  line: LineType = referenceTypeDefaultLineType,
  color: string = referenceTypeDefaultColor
): string => {
  const svgStyle = `stroke: ${color};`;
  const dasharray = lineTypeToStrokeDasharray(line);
  if (dasharray) return `${svgStyle}stroke-dasharray:${dasharray}`;
  return svgStyle;
};

export const getStyle = (
  referenceType: APIReferenceType | null
): string | null =>
  referenceType
    ? getReferenceTypeSvgStyle(referenceType.line, referenceType.color)
    : null;

export const getValidTypeByParent = (
  modelData: APIModelAttributes,
  typeId: string,
  parentTypeId: string | null
): APIComponentType | null => {
  const types = toComponentTypeDictionary(modelData.root);

  const allowedTypes = parentTypeId
    ? (types[parentTypeId]?.children ?? {})
    : modelData.root;

  const type = allowedTypes[typeId];
  if (type) return type;

  const firstType = Object.values(allowedTypes)[0];
  if (firstType) return firstType;

  if (modelData.flexible) {
    return Object.values(modelData.root)[0] ?? null;
  }

  return null;
};

export const getChildComponentTypes = (
  modelData: APIModelAttributes,
  parentTypeId: ArdoqId | null
): APIComponentType[] => {
  if (modelData.flexible) {
    return Object.values(getComponentTypes(modelData).componentTypesById);
  }
  if (!parentTypeId) return Object.values(modelData.root);
  const types = toComponentTypeDictionary(modelData.root);
  return Object.values(types[parentTypeId]?.children ?? {});
};
